import isPlainObject from 'lodash.isplainobject'

import { CRYPTO_TICKERS } from '../app/crypto/currencyUtils'
import { getCookie } from '../app/shared/cookie'
import { FeatureFlag, Instrument, SortDirection, SortableValue } from './types'
import { RecordType } from './types'

const isObject = (value: any): value is RecordType => isPlainObject(value)

/**
 * Parse text as JSON
 */
export function parseAsJSON<Shape>(value: unknown | Shape): Shape | undefined {
  if (isObject(value)) {
    return value as Shape
  }

  let parsedValue
  try {
    if (typeof value === 'string') {
      parsedValue = JSON.parse(value)
    }
  } catch {
    return
  }
  return parsedValue
}

/**
 * remove @ symbol from a ticker
 */
export function cleanTicker(ticker: string) {
  return ticker.startsWith('@') ? ticker.substring(1) : ticker
}

/**
 * Get instrument for a ticker.
 *
 * - If ticker doesn’t start with `@` - stock
 * - Otherwise
 *   - one of `CRYPTO_TICKERS` - crypto
 *   - has length of 6 - forex
 *   - none of above - future
 *
 * NOTE: counterpart in charts 'charts/app/utils/chart.js'
 */

export function getInstrumentForTicker(ticker: string): Instrument {
  if (!ticker?.startsWith('@')) return Instrument.Stock

  const cleanedTicker = cleanTicker(ticker)

  if (CRYPTO_TICKERS.includes(cleanedTicker.toUpperCase())) return Instrument.Crypto
  if (cleanedTicker.length === 6) return Instrument.Forex

  return Instrument.Futures
}

/**
 * Async load Resize observer polyfill when we need it
 */
let resizeObserverPolyfill: typeof window.ResizeObserver

export async function loadResizeObserverPolyfill() {
  if (typeof window.ResizeObserver === 'undefined') {
    const polyfill = await import('resize-observer-polyfill')
    resizeObserverPolyfill = polyfill.default
    window.ResizeObserver = resizeObserverPolyfill
    return resizeObserverPolyfill as typeof window.ResizeObserver
  }
}

export async function getIsBrave() {
  return (navigator.brave && (await navigator.brave.isBrave())) || false
}

export function deserializeFeatureFlags(): Partial<Record<FeatureFlag, boolean>> | undefined {
  try {
    const cookie = decodeURIComponent(getCookie('featureFlags'))
    const flags = cookie.split('_').map((value) => {
      const [key, val] = value.split(':')
      return [key, val === '1']
    })

    return Object.fromEntries(flags)
  } catch {
    return
  }
}

export function serializeFeatureFlags(flags: Partial<Record<FeatureFlag, boolean>>) {
  return Object.entries(flags)
    .map(([key, val]) => `${key}:${Number(val)}`)
    .join('_')
}

export enum ChildPosition {
  topLeft,
  topRight,
  bottomLeft,
  bottomRight,
  center,
  topCenter,
  rightCenter,
  bottomCenter,
  leftCenter,
}

export function getFlexAlignClasses(position: ChildPosition) {
  return {
    'justify-start': [ChildPosition.topLeft, ChildPosition.bottomLeft, ChildPosition.leftCenter].includes(position),
    'justify-center': [ChildPosition.center, ChildPosition.topCenter, ChildPosition.bottomCenter].includes(position),
    'justify-end': [ChildPosition.topRight, ChildPosition.bottomRight, ChildPosition.rightCenter].includes(position),
    'items-start': [ChildPosition.topLeft, ChildPosition.topRight, ChildPosition.topCenter].includes(position),
    'items-center': [ChildPosition.center, ChildPosition.rightCenter, ChildPosition.leftCenter].includes(position),
    'items-end': [ChildPosition.bottomLeft, ChildPosition.bottomRight, ChildPosition.bottomCenter].includes(position),
  }
}

interface NumberFormatOptions {
  fractions: number
  minFractions: number
  maxFractions: number
  multiply: number
  showPlusSign: boolean
  defaultValue: string
  prefix: string
  suffix: string
  withSpace: boolean
}

const intlCache: { [key: string]: Intl.NumberFormat } = {}
const dollarIntlCache: { [key: string]: Intl.NumberFormat } = {}

function getIntl({ maxFractions, minFractions = maxFractions }: { maxFractions: number; minFractions: number }) {
  const intlCacheKey = `${minFractions}-${maxFractions}`
  if (!intlCache[intlCacheKey])
    intlCache[intlCacheKey] = new Intl.NumberFormat('en-US', {
      minimumFractionDigits: minFractions,
      maximumFractionDigits: maxFractions,
    })
  return intlCache[intlCacheKey]
}

function getDollarIntl({ maxFractions, minFractions = maxFractions }: { maxFractions: number; minFractions: number }) {
  const dollarIntlCacheKey = `${minFractions}-${maxFractions}`
  if (!dollarIntlCache[dollarIntlCacheKey])
    dollarIntlCache[dollarIntlCacheKey] = new Intl.NumberFormat('en-US', {
      minimumFractionDigits: minFractions,
      maximumFractionDigits: maxFractions,
      style: 'currency',
      currency: 'USD',
    })
  return dollarIntlCache[dollarIntlCacheKey]
}

export function shortFormatNumber(
  value: number | null | undefined,
  {
    fractions = 2,
    maxFractions = fractions,
    minFractions = maxFractions,
    showPlusSign = false,
    defaultValue = '-',
    multiply = 1,
    suffix: extraSuffix = '',
  }: Partial<NumberFormatOptions> = {}
) {
  let num = value
  if (num === undefined || num === null || !Number.isFinite(num)) return defaultValue

  num *= multiply

  let suffix = ''
  const absValue = Math.abs(num)
  if (absValue >= 1e9) {
    num /= 1e9
    suffix = 'B'
  } else if (absValue >= 1e6) {
    num /= 1e6
    suffix = 'M'
  } else if (absValue >= 1000) {
    num /= 1000
    suffix = 'K'
  }

  const formatted = getIntl({ minFractions, maxFractions }).format(num)

  return (showPlusSign && num > 0 ? '+' : '') + formatted + suffix + extraSuffix
}

export function shortFormatDollar(
  value: number | null | undefined,
  {
    fractions = 2,
    maxFractions = fractions,
    minFractions = maxFractions,
    showPlusSign = false,
    defaultValue = '-',
    multiply = 1,
    suffix: extraSuffix = '',
  }: Partial<NumberFormatOptions> = {}
) {
  let num = value
  if (num === undefined || num === null || !Number.isFinite(num)) return defaultValue

  num *= multiply

  let suffix = ''
  const absValue = Math.abs(num)
  if (absValue >= 1e9) {
    num /= 1e9
    suffix = 'B'
  } else if (absValue >= 1e6) {
    num /= 1e6
    suffix = 'M'
  } else if (absValue >= 1000) {
    num /= 1000
    suffix = 'K'
  }

  const formatted = getDollarIntl({ minFractions, maxFractions }).format(num)

  return (showPlusSign && num > 0 ? '+' : '') + formatted + suffix + extraSuffix
}

export function getDecimalPlacesForValue(value: number, instrument: Instrument = Instrument.Stock) {
  if (value >= 100000) {
    return 0
  } else if (value >= 10000) {
    return 1
  } else if (instrument === Instrument.Stock && value < 1) {
    return 4
  } else if (value === undefined) {
    // if lastClose is not defined we'll always display 2 decimpal places, e.g. backtests, screener stats
    return 2
  }

  return undefined
}

export const FORMAT_PERCENT = { suffix: '%' }
export const FORMAT_PERCENT_CHANGE = { ...FORMAT_PERCENT, showPlusSign: true }

export function formatNumber(
  value: number | null | undefined,
  {
    fractions = 2,
    maxFractions = fractions,
    minFractions = maxFractions,
    showPlusSign = false,
    defaultValue = '-',
    suffix = '',
  }: Partial<NumberFormatOptions> = {}
) {
  if (value === undefined || value === null) return defaultValue

  const fixedValue = value.toFixed(maxFractions)
  const signPrefix = showPlusSign && value > 0 ? '+' : ''

  if (parseFloat(fixedValue) === 0 && value !== 0) {
    return <span title={value.toString() + suffix}>{signPrefix + fixedValue + suffix}</span>
  }

  const formatted = getIntl({ minFractions, maxFractions }).format(value)

  return signPrefix + formatted + suffix
}

export function formatDollar(
  value: number | null | undefined,
  {
    fractions = 2,
    maxFractions = fractions,
    minFractions = maxFractions,
    showPlusSign = false,
    defaultValue = '-',
    suffix = '',
  }: Partial<NumberFormatOptions> = {}
) {
  if (value === undefined || value === null) return defaultValue

  const fixedValue = value.toFixed(maxFractions)
  const signPrefix = showPlusSign && value > 0 ? '+' : ''

  const formatted = getDollarIntl({ minFractions, maxFractions }).format(value)

  if (parseFloat(fixedValue) === 0 && value !== 0) {
    return <span title={value.toString() + suffix}>{signPrefix + formatted + suffix}</span>
  }

  return signPrefix + formatted + suffix
}

export interface SortByColumnOptions {
  /**
   * Change order of `null` values.
   * - `false` Force sort null as first (asc) and last (desc)
   * - `true` Always sort null as last regardless of order
   * @default false
   */
  sortNullAsLast?: boolean

  /**
   * When true, null values will not be treated as special values
   * @default false
   */
  ignoreNullForComparison?: boolean
}

export function sortByColumn(
  options: SortByColumnOptions & {
    a: SortableValue
    b: SortableValue
    direction: SortDirection
  }
) {
  if (options.a === options.b) return 0

  // Compare as strings
  if (typeof options.a === 'string' && typeof options.b === 'string')
    return options.a.localeCompare(options.b) * options.direction

  // Always sort null last if enabled
  if (options.sortNullAsLast && options.a === null) return Infinity
  if (options.sortNullAsLast && options.b === null) return -Infinity

  // Sort null first/last depending on direction
  if (!options.ignoreNullForComparison && options.a === null) return -options.direction
  if (!options.ignoreNullForComparison && options.b === null) return options.direction

  // Compare as numbers
  return (Number(options.a) - Number(options.b)) * options.direction
}

export function moveItemInArray<ItemType>(arr: ItemType[], sourceIndex: number, destinationIndex: number) {
  // make sure sourceIndex and destinationIndex are inside of arr
  const from = Math.max(0, Math.min(sourceIndex, arr.length - 1))
  const to = Math.min(arr.length - 1, Math.max(destinationIndex, 0))

  const items = [...arr]
  const [removed] = items.splice(from, 1)
  items.splice(to, 0, removed)

  return items
}

export function randomUUID() {
  return `${1e7}-${1e3}-${4e3}-${8e3}-${1e11}`.replace(/[018]/g, (substring) => {
    const c = Number(substring)
    return (c ^ (crypto.getRandomValues(new Uint8Array(1))[0] & (15 >> (c / 4)))).toString(16)
  })
}

export function getUuid() {
  return window.crypto?.randomUUID?.() ?? randomUUID()
}

/**
 * Used as a callback to autoFocusOnShow/autoFocusOnHide to make sure the page
 * doesn’t jump when focusing the element
 */
export function focusWithoutScroll(element: HTMLElement | null) {
  requestAnimationFrame(() => {
    element?.focus({ preventScroll: element.ariaHasPopup !== null })
  })

  return !element
}

export function blurWithoutScroll(element: HTMLElement | null) {
  element?.focus({ preventScroll: true })

  return !element
}

/**
 * Can be used as a callback for events if you just need to prevent the default action
 */
export function preventDefault(ev: React.MouseEvent) {
  ev.preventDefault()
}

export function getNavigationLinkUrl(
  url: URL,
  preservedKeys?: string[],
  removeQueryKeys?: string[],
  additionalQueryValues: Record<string, string | number> = {}
) {
  const newUrl = new URL(url)

  if (preservedKeys?.length) {
    for (const key of url.searchParams.keys()) {
      if (!preservedKeys.includes(key)) newUrl.searchParams.delete(key)
    }
  }

  removeQueryKeys?.forEach((key) => {
    newUrl.searchParams.delete(key)
  })

  Object.entries(additionalQueryValues).forEach(([key, value]) => {
    newUrl.searchParams.set(key, value as string)
  })

  return newUrl
}

export function getElitePageLink(campaign?: string) {
  const url = '/elite.ashx'
  const query = new URLSearchParams('utm_source=finviz&utm_medium=banner')

  if (campaign) {
    query.set('utm_campaign', campaign)

    return `${url}?${query.toString()}`
  }

  return url
}

export function getNumberValue(value?: number) {
  if (value === undefined || isNaN(value)) return NaN
  return value
}

export function dateStrToDate(dateStr: string) {
  return new Date(`${dateStr}T00:00:00`)
}

export function dateStrToTimestamp(dateStr: string) {
  return dateStrToDate(dateStr).getTime()
}

export function getEarningsDateSuffix(date: Date | null) {
  if (!date) {
    return ''
  }
  const hours = date.getHours()
  if (hours <= 9) {
    return 'BMO'
  }
  if (hours >= 16) {
    return 'AMC'
  }
  return ''
}
